<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  currentStep: { type: Number, required: true },
  completedSteps: { type: Array, default: () => [] },
});

const { t } = useI18n();

const TOTAL_STEPS = 10;

const isReached = step =>
  step === props.currentStep || props.completedSteps.includes(step);

const dots = computed(() =>
  Array.from({ length: TOTAL_STEPS }, (_, index) => {
    const step = index + 1;
    return {
      step,
      isActive: step === props.currentStep,
      isComplete: props.completedSteps.includes(step),
    };
  })
);

// Each connector is a real flex item sized by the browser to exactly fill the
// gap between its two neighboring dots — no percentage/pixel math to keep in
// sync with the dots' own layout, and it stays correct at any viewport width
// or text zoom level. A connector is blue once the dot it leads into (the one
// on its right) has been reached, so the line always meets the dot with no
// gap and no overshoot past it.
const connectorIsBlue = index => isReached(dots.value[index + 1].step);

// The first/last dot sit flush with the ol's padding edge, so the decorative
// extension that reaches the screen edges (outside the dots entirely) needs
// its own blue/gray state once that end has been reached.
const startsAtFirstStep = computed(() => isReached(1));
const endsAtLastStep = computed(() => isReached(TOTAL_STEPS));
</script>

<template>
  <nav
    class="relative flex items-center w-full"
    :aria-label="t('ONBOARDING.PROGRESS_BAR.LABEL')"
  >
    <!-- Extends past the dots to reach the screen edges — the offset matches the
         px-8/md:px-16 padding of the header that wraps this component. -->
    <div
      class="absolute h-1 top-1/2 -translate-y-1/2 bg-n-slate-4 -left-8 -right-8 md:-left-16 md:-right-16"
    />
    <!-- Reaches 2rem past the nav edge — past the ol's px-3 inset and the end
         dot's own width — so it tucks fully behind the dot (z-10) instead of
         leaving a sliver of the gray track showing between them. -->
    <div
      v-if="startsAtFirstStep"
      class="absolute w-16 h-1 top-1/2 -translate-x-8 -translate-y-1/2 bg-n-brand left-0 md:w-24 md:-translate-x-16"
    />
    <div
      v-if="endsAtLastStep"
      class="absolute w-16 h-1 top-1/2 translate-x-8 -translate-y-1/2 bg-n-brand right-0 md:w-24 md:translate-x-16"
    />
    <ol class="relative z-10 flex items-center w-full px-3 m-0 list-none">
      <template v-for="(dot, index) in dots" :key="dot.step">
        <li
          class="flex items-center justify-center rounded-full transition-all shrink-0"
          :class="[
            dot.isActive ? 'w-5 h-5' : 'w-3.5 h-3.5',
            dot.isActive || dot.isComplete ? 'bg-n-brand' : 'bg-n-slate-4',
          ]"
        >
          <i
            v-if="dot.isComplete && !dot.isActive"
            class="text-white i-lucide-check text-[10px]"
          />
        </li>
        <div
          v-if="index < dots.length - 1"
          class="flex-1 h-1 transition-colors"
          :class="connectorIsBlue(index) ? 'bg-n-brand' : 'bg-n-slate-4'"
        />
      </template>
    </ol>
  </nav>
</template>
